/* eslint-disable @typescript-eslint/no-explicit-any */
import dcmjs from 'dcmjs';
import { vec3, ReadonlyMat4, ReadonlyVec3 } from 'gl-matrix';
import { roundToDecimals } from '../dicom/utils';
import { Grid } from '~/engine/data/common/Grid';
import AppConfig from '~/utils/appconfig';

const { DicomMetaDictionary } = dcmjs.data;

export interface VolumeGeometry {
    size: ReadonlyVec3; // [nx, ny, nz]
    res: ReadonlyVec3; // [dx, dy, dz] mm   (square in-plane from resampler)
    transform: ReadonlyMat4; // world = transform * [i, j, k, 1]
}

/**
 * Apply rescale slope and intercept to pixel values if present in dataset
 * For CT modality, defaults to slope=1, intercept=-1024 if not specified
 */
function applyRescaleTransformation(
    pixels: Int16Array,
    exampleDataset: any
): Int16Array {
    const modality = exampleDataset.Modality?.toUpperCase();

    // Determine rescale parameters
    let rescaleSlope = exampleDataset.RescaleSlope;
    let rescaleIntercept = exampleDataset.RescaleIntercept;

    // For CT modality, apply default values if not present
    if (modality === 'CT') {
        if (rescaleSlope === undefined || rescaleSlope === null) {
            rescaleSlope = 1;
        }
        if (rescaleIntercept === undefined || rescaleIntercept === null) {
            rescaleIntercept = -1024;
        }
    }

    // If no rescale parameters available, return original pixels
    if (
        rescaleSlope === undefined ||
        rescaleSlope === null ||
        rescaleIntercept === undefined ||
        rescaleIntercept === null
    ) {
        return pixels;
    }

    // Convert to numbers for calculations
    const slope = Number(rescaleSlope);
    const intercept = Number(rescaleIntercept);

    // Apply transformation: pixel_value = slope * stored_value + intercept
    // We need to reverse this to get stored values: stored_value = (pixel_value - intercept) / slope
    const transformedPixels = new Int16Array(pixels.length);

    for (let i = 0; i < pixels.length; i++) {
        const originalValue = pixels[i];
        // Reverse the rescale transformation to get stored pixel value
        const storedValue = (originalValue - intercept) / slope;
        // Clamp to Int16 range and round
        transformedPixels[i] = Math.max(
            -32768,
            Math.min(32767, Math.round(storedValue))
        );
    }

    return transformedPixels;
}

/**
 * @param preserveFrameOfReferenceUID  When provided, this UID is
 *   reused in the derived series instead of generating a new one.
 *   Pass the original FoR UID when the reformat preserves the
 *   spatial coordinate system (e.g. slice-thickness-only change)
 *   so that existing structure sets remain associated.
 */
export async function buildDerivedSeriesFromVolume(
    pixels: Int16Array,
    grid: Grid,
    exampleDataset: any,
    preserveFrameOfReferenceUID?: string
): Promise<any[]> {
    const nx = grid.sizeX;
    const ny = grid.sizeY;
    const nz = grid.sizeZ;

    if (pixels.length !== nx * ny * nz) {
        throw new Error('Pixel data length does not match geometry.size');
    }

    const T = grid.getTransformation();

    // Extract orientation from grid (supports non-HFS grids)
    const gridCorner = vec3.fromValues(T[12], T[13], T[14]);
    const unitX = grid.getUnitX();
    const unitY = grid.getUnitY();
    const sliceNormal = grid.getUnitZ();

    const rowDir: [number, number, number] = [unitX[0], unitX[1], unitX[2]];
    const colDir: [number, number, number] = [unitY[0], unitY[1], unitY[2]];

    const colSpacing = grid.resX; // dx
    const rowSpacing = grid.resY; // dy
    const sliceThickness = grid.resZ; // dz

    // Grid transformation origin is the corner of the first voxel.
    // DICOM ImagePositionPatient must be the center of the first
    // pixel in each slice: shift by half a voxel in all three axes.
    const ippOrigin = vec3.fromValues(
        gridCorner[0] +
            0.5 * colSpacing * rowDir[0] +
            0.5 * rowSpacing * colDir[0] +
            0.5 * sliceThickness * sliceNormal[0],
        gridCorner[1] +
            0.5 * colSpacing * rowDir[1] +
            0.5 * rowSpacing * colDir[1] +
            0.5 * sliceThickness * sliceNormal[1],
        gridCorner[2] +
            0.5 * colSpacing * rowDir[2] +
            0.5 * rowSpacing * colDir[2] +
            0.5 * sliceThickness * sliceNormal[2]
    );

    // --- Apply rescale transformation to pixel data ---
    const transformedPixels = applyRescaleTransformation(
        pixels,
        exampleDataset
    );

    // --- UIDs & shared attributes ---

    const studyInstanceUID: string =
        exampleDataset.StudyInstanceUID || DicomMetaDictionary.uid();

    const newSeriesInstanceUID: string = DicomMetaDictionary.uid();

    const frameOfReferenceUID: string =
        preserveFrameOfReferenceUID || DicomMetaDictionary.uid();

    const sopClassUID: string =
        exampleDataset.SOPClassUID || '1.2.840.10008.5.1.4.1.1.4'; // MR Image Storage

    // ImageType: DERIVED\SECONDARY\AXIAL
    const derivedImageType = ['DERIVED', 'SECONDARY', 'AXIAL'];

    const pixelRepresentation = exampleDataset.PixelRepresentation ?? 1; // 1 = signed
    const bitsAllocated = exampleDataset.BitsAllocated ?? 16;
    const bitsStored = exampleDataset.BitsStored ?? 16;
    const highBit =
        exampleDataset.HighBit ?? (bitsStored > 0 ? bitsStored - 1 : 15);

    const samplesPerPixel = exampleDataset.SamplesPerPixel ?? 1;
    const photometricInterpretation =
        exampleDataset.PhotometricInterpretation ?? 'MONOCHROME2';

    const rows = ny;
    const columns = nx;

    // Determine rescale parameters for output datasets
    const modality = exampleDataset.Modality?.toUpperCase();
    let outputRescaleSlope = exampleDataset.RescaleSlope;
    let outputRescaleIntercept = exampleDataset.RescaleIntercept;

    // For CT modality, ensure we have rescale parameters
    if (modality === 'CT') {
        if (outputRescaleSlope === undefined || outputRescaleSlope === null) {
            outputRescaleSlope = 1;
        }
        if (
            outputRescaleIntercept === undefined ||
            outputRescaleIntercept === null
        ) {
            outputRescaleIntercept = -1024;
        }
    }

    const datasets: any[] = [];
    const sliceSize = nx * ny;

    // Timestamps for derived series creation
    const now = new Date();
    const dateStr =
        `${now.getFullYear()}` +
        `${String(now.getMonth() + 1).padStart(2, '0')}` +
        `${String(now.getDate()).padStart(2, '0')}`;
    const timeStr =
        `${String(now.getHours()).padStart(2, '0')}` +
        `${String(now.getMinutes()).padStart(2, '0')}` +
        `${String(now.getSeconds()).padStart(2, '0')}`;

    for (let z = 0; z < nz; z++) {
        const sliceOffset = z * sliceSize;
        const sliceView = transformedPixels.subarray(
            sliceOffset,
            sliceOffset + sliceSize
        );
        const sliceBuffer = sliceView.buffer.slice(
            sliceView.byteOffset,
            sliceView.byteOffset + sliceView.byteLength
        );

        // IPP for slice z: voxel-center origin + z * sliceThickness * normal
        const ipp = vec3.fromValues(
            ippOrigin[0] + z * sliceThickness * sliceNormal[0],
            ippOrigin[1] + z * sliceThickness * sliceNormal[1],
            ippOrigin[2] + z * sliceThickness * sliceNormal[2]
        );

        const sopInstanceUID = DicomMetaDictionary.uid();

        // Start from a shallow copy of template to keep patient/study fields
        const ds: any = { ...exampleDataset };

        // Remove geometry & pixel-related tags we'll overwrite
        delete ds.PixelData;
        delete ds.Rows;
        delete ds.Columns;
        delete ds.ImagePositionPatient;
        delete ds.ImageOrientationPatient;
        delete ds.PixelSpacing;
        delete ds.SliceThickness;
        delete ds.SOPInstanceUID;
        delete ds.SeriesInstanceUID;
        delete ds.StudyInstanceUID;
        delete ds.FrameOfReferenceUID;
        delete ds.InstanceNumber;
        delete ds.ImageType;
        delete ds.BitsAllocated;
        delete ds.BitsStored;
        delete ds.HighBit;
        delete ds.PixelRepresentation;
        delete ds.SamplesPerPixel;
        delete ds.PhotometricInterpretation;
        delete ds.SpacingBetweenSlices;
        delete ds.SliceLocation;

        // --- Study / series / FoR ---

        ds.SeriesDescription =
            `Resampled ${ds.SeriesDescription?.substring(0, 20) || `${ds.Modality}`}`.trim();
        ds.StudyInstanceUID = studyInstanceUID;
        ds.SeriesInstanceUID = newSeriesInstanceUID;
        ds.FrameOfReferenceUID = frameOfReferenceUID;

        ds.SeriesNumber =
            typeof exampleDataset.SeriesNumber === 'number'
                ? exampleDataset.SeriesNumber + 1000
                : 1000;

        // --- SOP & common attributes ---

        ds.SOPClassUID = sopClassUID;
        ds.SOPInstanceUID = sopInstanceUID;
        ds.InstanceNumber = z + 1;

        ds.Modality = exampleDataset.Modality || 'MR';
        ds.ImageType = derivedImageType;
        ds.DerivationDescription =
            'Resampled to neutral axial HFS orientation (derived volume)';

        ds.Manufacturer = AppConfig.dicomManufacturer;
        ds.ManufacturerModelName = AppConfig.dicomManufacturerModelName;
        ds.SoftwareVersions = AppConfig.version;
        ds.SeriesDate = dateStr;
        ds.SeriesTime = timeStr;
        ds.InstanceCreationDate = dateStr;
        ds.InstanceCreationTime = timeStr;

        // PatientPosition (0018,5100) records how the patient was
        // physically positioned on the table (HFS, FFS, ...). It must
        // be preserved from the source — only ImageOrientationPatient
        // and ImagePositionPatient change for the reformatted plane.
        ds.PatientPosition = exampleDataset.PatientPosition ?? '';

        // --- Geometry ---

        ds.Rows = rows;
        ds.Columns = columns;

        ds.ImageOrientationPatient = [
            roundToDecimals(rowDir[0], 8),
            roundToDecimals(rowDir[1], 8),
            roundToDecimals(rowDir[2], 8),
            roundToDecimals(colDir[0], 8),
            roundToDecimals(colDir[1], 8),
            roundToDecimals(colDir[2], 8),
        ];

        ds.ImagePositionPatient = [
            roundToDecimals(ipp[0], 8),
            roundToDecimals(ipp[1], 8),
            roundToDecimals(ipp[2], 8),
        ];

        ds.PixelSpacing = [
            rowSpacing.toFixed(4), // [0] = row spacing
            colSpacing.toFixed(4), // [1] = column spacing
        ];
        ds.SliceThickness = sliceThickness.toFixed(4);
        ds.SpacingBetweenSlices = sliceThickness.toFixed(4);
        ds.SliceLocation = roundToDecimals(
            vec3.dot(sliceNormal as vec3, ipp),
            4
        );

        // --- Rescale parameters ---
        if (outputRescaleSlope !== undefined && outputRescaleSlope !== null) {
            ds.RescaleSlope = Number(outputRescaleSlope);
        }
        if (
            outputRescaleIntercept !== undefined &&
            outputRescaleIntercept !== null
        ) {
            ds.RescaleIntercept = Number(outputRescaleIntercept);
        }

        // --- Pixel module ---

        ds.SamplesPerPixel = samplesPerPixel;
        ds.PhotometricInterpretation = photometricInterpretation;
        ds.BitsAllocated = bitsAllocated;
        ds.BitsStored = bitsStored;
        ds.HighBit = highBit;
        ds.PixelRepresentation = pixelRepresentation;

        ds.PixelData = sliceBuffer;

        if (exampleDataset.SOPInstanceUID) {
            ds.SourceImageSequence = [
                {
                    ReferencedSOPClassUID: exampleDataset.SOPClassUID,
                    ReferencedSOPInstanceUID: exampleDataset.SOPInstanceUID,
                },
            ];
        }

        datasets.push(ds);
    }

    return datasets;
}
